import { useState, useMemo, useEffect } from 'react'
import { loanCapitalProgram, ltvLtcText } from '@/pages/pipeline-intelligence/dealResolution'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Shield,
  Users,
  ClipboardCheck,
  CircleDot,
  ChevronRight,
  Sparkles,
  Download,
} from 'lucide-react'
import { useAntarcticaDeals } from '@/hooks/useDemoDataAccess'
import { borrowingEntity } from '@/pages/pipeline-intelligence/helpers'
import { committeeCapRate, committeeDateLabel, committeeDebtYield } from '@/pages/committeeFigures'
import { reportsService } from '@/services/loans'
import { memoSectionLabel, memosService } from '@/services/memos'
import type { CommitteeMemo } from '@/types'

/* ─── helpers ─────────────────────────────────────────────────────────── */
/**
 * Loan amounts are stored in DOLLARS. This read them as millions, so a live
 * $41,500,000 request printed as "$41500000.0M" on the committee queue — the
 * same units error the backend assembler carried into the memo's own cover.
 * Demo fixtures happen to hold millions, so both are handled by magnitude.
 */
const fmt = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n) || n === 0) return '—'
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1) return `$${n.toFixed(1)}M`
  return `$${(n * 1000).toFixed(0)}K`
}

const fmtDate = committeeDateLabel

/** The stage a deal awaits IC at: the next stage is IC / Committee Approved. */
const AWAITING_IC_STAGE = 'Term Sheet Signed'
const NO_MEMO = 'No memo'

const memoStatusColor: Record<string, string> = {
  [NO_MEMO]: 'bg-transparent text-stone-500 border-stone-600/40',
  Draft: 'bg-stone-500/20 text-stone-300 border-stone-500/30',
  'In Review': 'bg-amber-400/10 text-amber-300 border-amber-400/20',
  Circulated: 'bg-blue-400/10 text-blue-300 border-blue-400/20',
  Approved: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
  Declined: 'bg-red-400/10 text-red-400 border-red-400/20',
}

const riskColor: Record<string, string> = {
  Low: 'text-emerald-400',
  Moderate: 'text-amber-400',
  Elevated: 'text-orange-400',
  High: 'text-red-400',
}

const riskBadge: Record<string, string> = {
  Low: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
  Moderate: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
  Elevated: 'bg-orange-400/10 text-orange-400 border-orange-400/20',
  High: 'bg-red-400/10 text-red-400 border-red-400/20',
}

/* ─── component ───────────────────────────────────────────────────────── */
export default function Committee() {
  const deals = useAntarcticaDeals('committee')
  const queryClient = useQueryClient()

  // Memos live in `committee_memos` and are read from the endpoint that owns
  // them. The page used to filter deals on a `committeeMemo` field the loans
  // API has never returned, so against a live workspace the queue was always
  // empty and only demo fixtures populated it.
  const memosQuery = useQuery({
    queryKey: ['committee-memos'],
    queryFn: () => memosService.listCommittee(),
    staleTime: 30_000,
  })
  const serverMemos = memosQuery.data ?? []

  const committeeDeals = useMemo(() => {
    const withServerMemo = serverMemos
      .map((m) => deals.find((d) => d.id === m.loanId))
      .filter((d): d is NonNullable<typeof d> => Boolean(d))
    // Demo fixtures carry the memo inline; live deals do not.
    const fixtures = deals.filter((d) => d.committeeMemo)
    // A deal at Term Sheet Signed is awaiting IC whether or not anyone has
    // drafted its memo yet: the stage model has deliberately no stage between
    // Term Sheet Signed and IC / Committee Approved (LoanStage, backend). The
    // queue listed only deals with a memo on file, so with four signed deals
    // on the live book and no memo drafted it read "0 deals" (2 Nov 2026).
    const awaitingIc = deals.filter((d) => d.stage === AWAITING_IC_STAGE)
    const seen = new Set<string>()
    return [...withServerMemo, ...fixtures, ...awaitingIc].filter((d) => {
      if (seen.has(d.id)) return false
      seen.add(d.id)
      return true
    })
  }, [deals, serverMemos])

  const [selectedId, setSelectedId] = useState<string>('')
  useEffect(() => {
    if (!selectedId && committeeDeals[0]) setSelectedId(committeeDeals[0].id)
  }, [committeeDeals, selectedId])

  const selected = committeeDeals.find((d) => d.id === selectedId) ?? committeeDeals[0]
  /** The memo for a deal, from the record or from a demo fixture. Never
   *  assume one source: the queue now mixes both. */
  const memoRowFor = (dealId: string) => {
    const fixture = deals.find((d) => d.id === dealId)?.committeeMemo as
      | CommitteeMemo
      | undefined
    if (fixture) {
      return {
        status: fixture.status,
        committeeDate: fixture.committeeDate,
        riskRating: fixture.riskRating,
        conditionCount: fixture.conditions.length,
      }
    }
    const server = serverMemos.find((m) => m.loanId === dealId)
    return {
      // A deal awaiting IC with no memo drafted has no memo, not a Draft one.
      status: server?.status ?? NO_MEMO,
      committeeDate: server?.committeeDate ?? '',
      // A risk rating is a credit judgement somebody makes. Nothing on the
      // record assigns one, so a live memo shows none rather than inheriting
      // the demo fixture's "Moderate".
      riskRating: null as string | null,
      conditionCount: (server?.conditionsToApproval ?? []).length,
    }
  }

  const serverMemo = serverMemos.find((m) => m.loanId === selected?.id) ?? null
  const memo = (selected?.committeeMemo as CommitteeMemo | undefined) ?? (serverMemo
    ? ({
        dealId: serverMemo.loanId,
        status: serverMemo.status,
        preparedBy: serverMemo.generatedBy ?? 'Not recorded',
        committeeDate: serverMemo.committeeDate ?? '',
        riskRating: '',
        conditions: (serverMemo.conditionsToApproval ?? []).map(String),
        approvers: [],
        sections: {
          executiveSummary: serverMemo.executiveSummary ?? '',
          propertyDescription: serverMemo.propertyDescription ?? '',
          marketAnalysis: serverMemo.marketAnalysis ?? '',
          cashFlowSummary: '',
          structure: '',
          riskFactors: '',
          recommendation: serverMemo.recommendation ?? '',
        },
      } as unknown as CommitteeMemo)
    : undefined)

  // Auto-Assemble: draw every section the deal file already holds into the
  // memo, and state plainly which sections a person still owes. It was a
  // 1.8-second timer followed by a hardcoded sentence naming five sections as
  // populated whatever the deal contained.
  const assembleMutation = useMutation({
    mutationFn: () =>
      memosService.assembleCommittee({
        loanId: selected!.id,
        committeeDate: serverMemo?.committeeDate ?? null,
        committeeType: serverMemo?.committeeType ?? 'IC',
        conditionsToApproval: (serverMemo?.conditionsToApproval ?? []).map(String),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-memos'] })
    },
  })
  const generating = assembleMutation.isPending
  const assembled = assembleMutation.data ?? serverMemo
  const generated = Boolean(assembleMutation.data)

  const handleGenerate = () => {
    if (!selected) return
    assembleMutation.mutate()
  }

  // Print this sitting's papers through the report engine. The Committee Book
  // builder reproduces the recorded memoranda; a memo section with nothing in
  // it prints as "none recorded" rather than being filled in here.
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const handleExport = async () => {
    if (!memo) return
    setExporting(true)
    setExportError(null)
    try {
      const response = await reportsService.generate({
        template: 'committee_book',
        filters: memo.committeeDate
          ? { committee_date: memo.committeeDate }
          : { loan_ids: [selected.id] },
        format: 'pdf',
      })
      const blob = response.data as Blob
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error('The report engine returned an empty document.')
      }
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `committee-book-${memo.committeeDate || selected.id}.pdf`
      anchor.rel = 'noopener'
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
    } catch (cause) {
      const detail = (cause as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setExportError(detail ?? (cause instanceof Error ? cause.message : 'The committee book could not be produced.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="px-4 py-3 space-y-3 h-full">
      {/* ═══ two-column layout ═══ */}
      <div className="grid grid-cols-[45fr_55fr] gap-3 h-[calc(100vh-6rem)]">
        {/* ─── LEFT: queue + approvers + conditions ─── */}
        <div className="flex flex-col gap-3 min-h-0">
          {/* committee queue */}
          <div className="panel flex-1 min-h-0 flex flex-col">
            <div className="panel-header">
              <span className="panel-header-title flex items-center gap-2">
                <Users size={13} className="text-amber-400/60" />
                Committee Queue
              </span>
              <span className="text-[10px] text-stone-500 font-mono">
                {committeeDeals.length} deals
              </span>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th className="text-right">Amount</th>
                    <th>Product</th>
                    <th>Committee</th>
                    <th>Memo</th>
                    <th>Risk</th>
                    <th className="text-right">Cond.</th>
                  </tr>
                </thead>
                <tbody>
                  {committeeDeals.map((deal) => {
                    const m = memoRowFor(deal.id)
                    return (
                      <tr
                        key={deal.id}
                        className={`cursor-pointer ${deal.id === selectedId ? 'selected' : ''}`}
                        onClick={() => setSelectedId(deal.id)}
                      >
                        <td className="max-w-[160px] truncate">
                          <div className="flex items-center gap-1.5">
                            {deal.id === selectedId && (
                              <ChevronRight size={10} className="text-amber-400 shrink-0" />
                            )}
                            <span className={deal.id === selectedId ? 'text-amber-300' : ''}>
                              {deal.property}
                            </span>
                          </div>
                        </td>
                        <td className="text-right font-mono text-[11px]">{fmt(deal.loanAmount)}</td>
                        <td className="text-[11px]">{loanCapitalProgram(deal) ?? '—'}</td>
                        <td className="text-[11px] text-stone-400">{fmtDate(m.committeeDate)}</td>
                        <td>
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${memoStatusColor[m.status]}`}
                          >
                            {m.status}
                          </span>
                        </td>
                        <td>
                          {m.riskRating ? (
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${riskBadge[m.riskRating]}`}
                            >
                              {m.riskRating}
                            </span>
                          ) : (
                            <span className="text-[10px] text-stone-600" title="No risk rating has been assigned">
                              —
                            </span>
                          )}
                        </td>
                        <td className="text-right font-mono text-[11px]">{m.conditionCount}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* approver status */}
          {memo && (
            <div className="panel">
              <div className="panel-header">
                <span className="panel-header-title flex items-center gap-2">
                  <Shield size={13} className="text-amber-400/60" />
                  Approver Status
                </span>
              </div>
              <div className="panel-body space-y-1.5">
                {memo.approvers.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-md border border-amber-400/10 bg-black/20 px-3 py-2"
                  >
                    <div>
                      <div className="text-xs text-stone-200 font-medium">{a.name}</div>
                      <div className="text-[10px] text-stone-500">{a.role}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {a.voted ? (
                        <>
                          {a.vote === 'Approve' && (
                            <CheckCircle2 size={14} className="text-emerald-400" />
                          )}
                          {a.vote === 'Conditional' && (
                            <AlertTriangle size={14} className="text-amber-400" />
                          )}
                          {a.vote === 'Decline' && (
                            <AlertTriangle size={14} className="text-red-400" />
                          )}
                          <span
                            className={`text-[11px] font-medium ${
                              a.vote === 'Approve'
                                ? 'text-emerald-400'
                                : a.vote === 'Conditional'
                                ? 'text-amber-400'
                                : 'text-red-400'
                            }`}
                          >
                            {a.vote === 'Conditional' ? 'Cond. Approve' : a.vote}
                          </span>
                        </>
                      ) : (
                        <>
                          <Clock size={14} className="text-stone-500" />
                          <span className="text-[11px] text-stone-500">Pending</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* conditions precedent */}
          {memo && (
            <div className="panel">
              <div className="panel-header">
                <span className="panel-header-title flex items-center gap-2">
                  <ClipboardCheck size={13} className="text-amber-400/60" />
                  Conditions Precedent
                </span>
              </div>
              <div className="panel-body space-y-1.5">
                {memo.conditions.map((c, i) => (
                  <label
                    key={i}
                    className="flex items-start gap-2 text-xs text-stone-300 py-1 cursor-default"
                  >
                    <CircleDot
                      size={14}
                      className={`shrink-0 mt-0.5 ${
                        i % 3 === 0 ? 'text-emerald-400' : 'text-stone-600'
                      }`}
                    />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── RIGHT: credit memo viewer ─── */}
        <div className="panel min-h-0 flex flex-col">
          {memo && selected ? (
            <>
              {/* header */}
              <div className="panel-header flex-col items-start gap-1 !py-3">
                <div className="flex items-center gap-3 w-full">
                  <FileText size={16} className="text-amber-400/70" />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-stone-100 truncate">
                      {selected.property}
                    </h2>
                    <span className="text-[10px] font-mono text-stone-500">
                      {selected.id} &middot; {borrowingEntity(selected) || 'Borrowing entity not stated'}
                    </span>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-medium border ${memoStatusColor[memo.status]}`}
                  >
                    {memo.status}
                  </span>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-amber-400/20 bg-amber-400/[0.06] text-[10px] text-amber-300 hover:bg-amber-400/10 transition-colors"
                  >
                    {generating ? (
                      <><div className="w-3 h-3 border border-amber-400/30 border-t-amber-400 rounded-full animate-spin" /> Assembling...</>
                    ) : (
                      <><Sparkles size={10} /> {generated ? 'Re-generate' : 'Auto-Assemble'}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleExport()}
                    disabled={exporting}
                    title="Print this sitting's committee book"
                    className="flex items-center gap-1 text-[10px] text-stone-500 hover:text-stone-300 disabled:opacity-40"
                  >
                    <Download size={10} /> {exporting ? 'Printing…' : 'Export'}
                  </button>
                </div>
                <div className="flex items-center gap-4 mt-2 text-[10px] text-stone-500">
                  <span>
                    Risk:{' '}
                    <span className={`font-medium ${memo.riskRating ? riskColor[memo.riskRating] : 'text-stone-600'}`}>
                      {memo.riskRating || 'Not assigned'}
                    </span>
                  </span>
                  <span>Committee: {fmtDate(memo.committeeDate)}</span>
                  <span>Prepared by: {memo.preparedBy}</span>
                </div>
                {exportError && (
                  <div role="alert" className="mt-2 w-full rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-2 text-[10px] text-red-200">
                    {exportError}
                  </div>
                )}
                {/* Auto-assembly status (Item 9) */}
                {assembleMutation.isError && (
                  <div role="alert" className="mt-2 w-full rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-2 text-[10px] text-red-200">
                    {(assembleMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                      ?? 'The memo could not be assembled.'}
                  </div>
                )}
                {/* What the assembly actually did, from the memo it produced —
                    not a fixed sentence. A section counts as populated only
                    when the deal file supplied it. */}
                {assembled && (
                  <div className="mt-2 w-full rounded-lg border border-emerald-400/20 bg-emerald-400/[0.04] px-2.5 py-2">
                    <div className="flex items-start gap-2">
                      <Sparkles size={11} className="mt-0.5 shrink-0 text-emerald-400" />
                      <div className="text-[10px] leading-relaxed text-stone-400">
                        <span className="font-medium text-emerald-400">
                          {generated ? 'Assembled' : 'Last assembled'}
                        </span>{' '}
                        from the deal file
                        {assembled.generatedAt
                          ? ` on ${new Date(assembled.generatedAt).toLocaleDateString()}`
                          : ''}
                        .
                      </div>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-emerald-300/80">
                          From the record ({assembled.sectionsPopulated.length})
                        </div>
                        {assembled.sectionsPopulated.length ? (
                          <ul className="mt-1 space-y-0.5">
                            {assembled.sectionsPopulated.map((key) => (
                              <li key={key} className="text-[10px] text-stone-300">
                                {memoSectionLabel(key)}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-1 text-[10px] italic text-stone-500">
                            The deal file supplied nothing yet.
                          </p>
                        )}
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-amber-300/80">
                          Still owed ({assembled.sectionsRequiringManualInput.length})
                        </div>
                        {assembled.sectionsRequiringManualInput.length ? (
                          <ul className="mt-1 space-y-0.5">
                            {assembled.sectionsRequiringManualInput.map((key) => (
                              <li key={key} className="text-[10px] text-amber-200/90">
                                {memoSectionLabel(key)}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-1 text-[10px] italic text-stone-500">
                            Nothing outstanding.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* metrics row */}
              <div className="grid grid-cols-5 gap-2 px-3 py-2 border-b border-amber-400/10">
                {[
                  // Every one of these is optional on a live deal. The cap-rate
                  // cell guarded on `cashFlows` and then read `market.capRate`,
                  // so the first live deal to reach this panel crashed the page.
                  // A metric the record does not carry reads as a dash, not as
                  // "N/A" — the deal is not inapplicable, it is unstated.
                  { label: 'Loan Amount', value: selected.loanAmount ? fmt(selected.loanAmount) : '—' },
                  // LTV-else-LTC, labeled by what it is, as the Pipeline prints it.
                  { label: 'LTV / LTC', value: ltvLtcText(selected) ?? '—' },
                  { label: 'DSCR', value: selected.dscr ? `${selected.dscr.toFixed(2)}x` : '—' },
                  { label: 'Debt Yield', value: committeeDebtYield(selected) },
                  { label: 'Cap Rate', value: committeeCapRate(selected) },
                ].map((m) => (
                  <div key={m.label} className="metric-cell text-center">
                    <div className="metric-cell-label">{m.label}</div>
                    <div className="metric-cell-value text-sm">{m.value}</div>
                  </div>
                ))}
              </div>

              {/* memo sections */}
              <div className="flex-1 overflow-auto px-4 py-3 space-y-4">
                {([
                  ['Executive Summary', memo.sections.executiveSummary],
                  ['Property Description', memo.sections.propertyDescription],
                  ['Market Analysis', memo.sections.marketAnalysis],
                  ['Cash Flow Summary', memo.sections.cashFlowSummary],
                  ['Structure', memo.sections.structure],
                  ['Risk Factors', memo.sections.riskFactors],
                  ['Recommendation', memo.sections.recommendation],
                ] as [string, string][]).map(([title, body]) => (
                  <section key={title}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="h-px flex-1 bg-gradient-to-r from-amber-400/20 to-transparent" />
                      <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-500 shrink-0">
                        {title}
                      </h3>
                      <div className="h-px flex-1 bg-gradient-to-l from-amber-400/20 to-transparent" />
                    </div>
                    <p className="text-xs leading-relaxed text-stone-300">{body}</p>
                  </section>
                ))}
              </div>
            </>
          ) : selected ? (
            // A deal awaiting IC with no memo drafted yet: say so, show what the
            // record holds, and offer the assembly that drafts one. This read
            // "Select a deal" while a deal was selected.
            <div className="flex-1 flex flex-col">
              <div className="panel-header flex-col items-start gap-1 !py-3">
                <div className="flex items-center gap-3 w-full">
                  <FileText size={16} className="text-amber-400/70" />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-stone-100 truncate">{selected.property}</h2>
                    <span className="text-[10px] font-mono text-stone-500">
                      {selected.stage} · {loanCapitalProgram(selected) ?? 'Capital program not recorded'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating}
                    className="flex items-center gap-1 text-[10px] text-amber-400 hover:text-amber-300 disabled:opacity-40"
                  >
                    {generating ? 'Assembling…' : <><Sparkles size={10} /> Auto-Assemble</>}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 px-3 py-2 border-b border-amber-400/10">
                {[
                  { label: 'Loan Amount', value: selected.loanAmount ? fmt(selected.loanAmount) : '—' },
                  { label: 'LTV / LTC', value: ltvLtcText(selected) ?? '—' },
                  { label: 'Debt Yield', value: committeeDebtYield(selected) },
                ].map((m) => (
                  <div key={m.label} className="metric-cell text-center">
                    <div className="metric-cell-label">{m.label}</div>
                    <div className="metric-cell-value text-sm">{m.value}</div>
                  </div>
                ))}
              </div>
              <div className="flex-1 flex items-center justify-center px-6 text-center text-stone-500 text-xs">
                No credit memo has been drafted for this deal yet. Auto-Assemble draws every section the
                deal file already holds into a draft and names the sections a person still owes.
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-600 text-sm">
              Select a deal to view its credit memo.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
